import { NavLink, useNavigate } from 'react-router-dom';
import { NAV, RANGES } from '@/app/nav';

/**
 * 사이드바 점 색 — ★ **화면의 축색과 같은 값**이다.
 *
 * ⚠️ `nav.ts` 에 넣지 않는다 — 저 표는 「무엇이 있는가」이고 색은 「어떻게
 * 보이는가」다. 섞으면 이식 1판의 경로 대조표가 디자인 변경마다 흔들린다.
 */
/**
 * 사이드바 아이콘 — ★ 색과 **같은 자리**에 둔다.
 *
 * ⚠️ 이모지가 아니라 SVG 다(lucide). SUIT 에 이모지가 없어 이모지를 쓰면
 * 폴백 폰트로 깨진다 — 이 프로젝트에서 이미 겪은 함정이다.
 */
const NAV_ICON: Record<string, LucideIcon> = {
  home: LayoutDashboard,
  completion: Flag,
  blocked: Ban,
  narration: MessageSquareWarning,
  cost: Coins,
  runs: ListOrdered,
  scenes: Clapperboard,
  settings: Settings,
  engagement: Users,
  aiQuality: Sparkles,
  versionCompare: GitCompare,
  opsMonitor: Activity,
  soro: Link2,
};

const NAV_COLOR: Record<string, string> = {
  home: 'var(--lens-primary)',
  completion: 'var(--lens-axis-completion)',
  blocked: 'var(--lens-axis-blocked)',
  narration: 'var(--lens-axis-narration)',
  cost: 'var(--lens-axis-cost)',
  runs: 'var(--lens-axis-runs)',
  scenes: 'var(--lens-axis-scenes)',
  settings: 'var(--lens-axis-settings)',
};
import {
  Activity,
  Ban,
  Clapperboard,
  Coins,
  Flag,
  GitCompare,
  LayoutDashboard,
  Link2,
  ListOrdered,
  LogOut,
  MessageSquareWarning,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/data/auth';
import { LensSymbol, LensWordmark } from '@/components/Brand';
import { Info } from '@/components/Info';
import { cn } from '@/lib/utils';

/**
 * 셸 — 사이드바 + 헤더.
 *
 * ⚠️ Flutter 쪽은 `AppSidebar` 1,677줄이다(FF 가 항목마다 트리를 복제했다).
 * ★ 여기서는 **배열 + map** 이다. 항목을 늘리려면 `src/app/nav.ts` 만 고친다.
 */

export function AppShell({
  title,
  subtitle,
  accent,
  rangeKey,
  onRangeChange,
  children,
}: {
  title: string;
  subtitle: string;
  /**
   * 축색 — ★★ **헤더 띠와 제목이 이 색을 쓴다.**
   * 「각 섹션이 뭘 모니터링하는지 구분이 안 된다」를 색으로 먼저 말한다.
   */
  accent?: string | undefined;
  /** 비면 기간 칩을 숨긴다. ★ 엔티티 화면(회차/장면 상세)과 설정이 그렇다. */
  rangeKey?: string;
  onRangeChange?: (key: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="min-w-0 flex-1">
        <header className="relative border-b border-border bg-card px-gutter py-xl">
          {/* ★ 축색 띠 — 화면을 옮길 때 **색이 먼저 바뀐다.** */}
          <span
            className="absolute inset-x-0 top-0 h-1.5"
            style={{ background: accent ?? 'var(--lens-border)' }}
            aria-hidden="true"
          />
          <div className="mx-auto flex max-w-[1360px] items-start justify-between gap-xl">
            <div className="flex min-w-0 items-center gap-md">
              {/* ⚠️ 설명을 제목 옆 툴팁으로 치웠다 — 전에는 긴 문장이 늘 떠 있었다. */}
              <h1
                className="text-headline-sm"
                style={accent !== undefined ? { color: accent } : undefined}
              >
                {title}
              </h1>
              <Info text={subtitle} label={`${title} 설명`} />
            </div>
            {rangeKey !== undefined && onRangeChange !== undefined ? (
              <RangeChips value={rangeKey} onChange={onRangeChange} />
            ) : null}
          </div>
        </header>
        {/* ⚠️ **폭을 가둔다.** 1600px 를 꽉 채우면 막대가 너무 길어 눈이 멀리
            가고 「한눈에 안 들어온다」가 된다(사용자 지적). */}
        <div className="mx-auto max-w-[1360px] px-gutter py-xl">{children}</div>
      </main>
    </div>
  );
}

/**
 * 사이드바 — ★★ **2026-10-04 전면 교체.**
 *
 * ⚠️ 앞선 개편에서 배경색과 아이콘만 바뀌고 **구조는 그대로**였다. 본문이
 * 계기판이 됐는데 레일만 예전 모습이라 전체를 견줄 수가 없었다(사용자 지적).
 *
 * 바뀐 것 넷:
 *   ① 조리개 마크 — LENS 의 이름이 곧 렌즈다. 브랜드가 글자뿐이었다.
 *   ② 활성 항목이 **축색 알약**이 된다 — 회색 띠가 아니라 그 화면의 색이다.
 *   ③ 구획 머리에 **짧은 선**을 둬 목록이 한 덩어리로 뭉치지 않게 한다.
 *   ④ 계정 칸이 **아바타 + 로그아웃 단추**가 된다.
 */
function Sidebar() {
  return (
    <nav className="flex w-sidebar shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      <BrandMark />
      <div className="min-h-0 flex-1 overflow-y-auto pb-xl">
        {NAV.map((section, si) => (
          <div key={section.title} className="pt-lg">
            <div className="flex items-center gap-sm px-panel pb-sm">
              <span className="text-label-sm tracking-wider text-sidebar-muted">
                {section.title}
              </span>
              {/* ★ 짧은 선 — 구획이 눈에 걸린다. 없으면 목록이 한 덩어리다. */}
              <span className="h-px min-w-0 flex-1 bg-sidebar-border" aria-hidden="true" />
            </div>
            {section.items.map((item, ii) =>
              item.path === null ? (
                // ★ Phase 2 — 메뉴에 실재하되 비활성이다. 자리를 미리 잡아
                //   정보 구조를 고정한다.
                <div
                  key={item.key}
                  className="mx-sm flex items-center gap-md rounded-md px-md py-sm text-title-sm text-sidebar-muted"
                  aria-disabled
                >
                  <Phase2Icon icon={NAV_ICON[item.key]} />
                  <span className="min-w-0 flex-1 truncate opacity-70">{item.label}</span>
                  <span className="shrink-0 rounded-pill border border-sidebar-border px-sm py-xs text-label-sm opacity-60">
                    준비 중
                  </span>
                </div>
              ) : (
                <NavLink
                  key={item.key}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    cn(
                      'animate-fade relative mx-sm flex items-center gap-md rounded-md px-md py-sm text-title-sm transition-colors',
                      // NavLink 가 접두 일치까지 봐 준다(`end` 는 루트에만).
                      isActive
                        ? 'text-sidebar-accent-foreground'
                        : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
                    )
                  }
                  style={{ animationDelay: `${(si * 5 + ii) * 25}ms` }}
                >
                  {({ isActive }) => {
                    const c = NAV_COLOR[item.key] ?? 'var(--lens-axis-settings)';
                    const Icon = NAV_ICON[item.key] ?? LayoutDashboard;
                    return (
                      <>
                        {/* ★★ 활성 항목은 **그 화면의 축색으로 칠한 알약**이다.
                            ⚠️ 회색 띠였을 때는 사이드바만 보고 어느 화면인지
                            알 수 없었다. 색은 `--lens-axis-*` 한 곳에서 나온다. */}
                        {isActive ? (
                          <span
                            className="absolute inset-0 rounded-md"
                            style={{
                              background: `linear-gradient(90deg, color-mix(in srgb, ${c} 22%, transparent), color-mix(in srgb, ${c} 6%, transparent))`,
                              boxShadow: `inset 2px 0 0 0 ${c}`,
                            }}
                            aria-hidden="true"
                          />
                        ) : null}
                        {/* ★ 아이콘이 축색을 입는다 — 비활성은 흐리게. */}
                        <Icon
                          size={18}
                          strokeWidth={1.9}
                          aria-hidden="true"
                          style={{ color: c, opacity: isActive ? 1 : 0.55 }}
                          className="relative shrink-0 transition-opacity"
                        />
                        <span className="relative truncate">{item.label}</span>
                      </>
                    );
                  }}
                </NavLink>
              ),
            )}
          </div>
        ))}
      </div>
      <AccountFooter />
    </nav>
  );
}

/**
 * 브랜드 — 조리개 마크 + 워드마크.
 *
 * ★ LENS 의 이름이 곧 렌즈다. Flutter 쪽은 `LensLogo` 를 `CustomPainter` 로
 *   벡터화했는데(에셋 업로드 API 가 없어서), 여기서는 lucide 의 `Aperture`
 *   가 같은 모양이라 그대로 쓴다 — 새 에셋이 필요 없다.
 * ⚠️ 이모지가 아니다 — SUIT 에 이모지가 없어 깨진다.
 */
function BrandMark() {
  return (
    <div className="flex items-center gap-md border-b border-sidebar-border px-panel py-xl">
      {/* ★ 실제 로고다 — 전에는 lucide 의 Aperture 를 임시로 썼다. */}
      <LensSymbol size={34} className="shrink-0" />
      {/* ⚠️ 부제(「OBXYD 운영 분석」)를 뺐다 — 사용자 지시(2026-10-04).
          ★ 로고가 이미 제품을 말한다. */}
      {/* ★ 사이드바가 테마를 따르므로 워드마크도 테마로 가른다(기본 `surface`).
          ⚠️ 전에는 `tone="onDark"` 로 흰판을 고정했다 — 사이드바가 늘 어두웠기
          때문이다. 그 전제가 없어졌다. */}
      <LensWordmark height={22} />
    </div>
  );
}

/**
 * 계정 칸 — 아바타 + 메일 + 로그아웃 단추.
 *
 * ⚠️ 로그인하지 않았으면 **아무것도 그리지 않는다.** 미리보기와 화면 테스트가
 * 로그인 없이 페이지를 띄우므로 여기서 죽으면 안 된다.
 */
function AccountFooter() {
  const { user, signOutNow } = useAuth();
  if (user === null) return null;
  const email = user.email ?? '(이메일 없음)';
  return (
    <div className="mt-auto flex items-center gap-md border-t border-sidebar-border px-panel py-lg">
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-pill text-label-md uppercase"
        style={{
          background: 'color-mix(in srgb, var(--lens-primary) 24%, transparent)',
          color: 'var(--lens-primary)',
        }}
        aria-hidden="true"
      >
        {email.slice(0, 2)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-label-md" title={email}>
          {email}
        </span>
        <span className="block text-label-sm text-sidebar-muted">로그인됨</span>
      </span>
      <button
        onClick={() => void signOutNow()}
        aria-label="로그아웃"
        title="로그아웃"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
      >
        <LogOut size={16} strokeWidth={1.9} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * 기간 칩.
 *
 * ⚠️⚠️ **상태를 바꾸는 위젯은 그 상태를 읽는 것까지 다시 돌려야 한다.**
 * Flutter 쪽에서는 App State 만 세우고 `onLoad` 를 다시 안 돌려 화면이 옛 값을
 * 보여준 적이 있다 — ★ React 에서는 상태가 올라가면 집계가 **자동으로** 다시
 * 돈다(`useLensData` 가 `rangeKey` 에 의존한다). 이 함정이 구조적으로 사라졌다.
 */
function RangeChips({ value, onChange }: { value: string; onChange: (k: string) => void }) {
  return (
    <div className="flex shrink-0 items-center gap-sm">
      <span className="text-label-md text-muted-foreground">기간</span>
      {RANGES.map((r) => (
        <button
          key={r.key}
          onClick={() => onChange(r.key)}
          aria-pressed={value === r.key}
          className={cn(
            'rounded-pill border px-md py-xs text-label-md transition-colors',
            value === r.key
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-muted-foreground hover:text-foreground',
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

/** 라우트 전환을 코드로 할 때. */
export const useGoto = () => useNavigate();

/** Phase 2 항목의 흐린 아이콘. ★ 자리를 비워 두면 목록이 어긋나 보인다. */
function Phase2Icon({ icon: Icon }: { icon?: LucideIcon | undefined }) {
  if (Icon === undefined) return <span className="h-[18px] w-[18px] shrink-0" />;
  return <Icon size={18} strokeWidth={1.9} aria-hidden="true" className="shrink-0 opacity-35" />;
}
